// Function index:
//   bindGameWS({ ws, chat, banner, pauseBanner, scene, selfCharId, charNames, npcNames,
//               onSelfHpChange, onCombatChange })
//     — wire every server-side outbound event the game cares about onto the
//       open WS connection. Returns a cleanup function the caller invokes on
//       world-mount teardown so stale handlers (which capture the destroyed
//       scene) don't fire on the next event.
//     Handles: system.message, rule.established, rule.amended, rules.paused, rules.resumed,
//       party.joined, presence.update, gm.narrate.*,
//       character.moved/joined_party/left_party/hp_changed/leveled,
//       hex.discovered, hex.condition.changed, location.arrived, npc.moved/state.changed/hp_changed/died,
//       combat.started/ended/turn/dice_rolled.
//     chronicle.recap is party-scoped and bound directly in runtime.ts —
//     it must survive before any world mount, so it isn't wired here.

import type { CombatSnapshot } from '../api.js';
import type { ChatMount, PresenceUser } from '../chat.js';
import type { BannerHandle } from '../combat-banner.js';
import type { SceneMount } from '../hex-scene.js';
import type { RulesPauseBannerHandle } from '../rules-pause-banner.js';
import type { WS } from '../ws.js';

export interface BindOpts {
  ws: WS;
  chat: ChatMount;
  banner: BannerHandle;
  pauseBanner: RulesPauseBannerHandle;
  scene: SceneMount;
  selfCharId: number | null;
  charNames: Map<number, string>;
  npcNames: Map<number, string>;
  onSelfHpChange: (hp: number, max_hp: number) => void;
  onCombatChange: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Handler = (payload: any) => void;

export function bindGameWS(o: BindOpts): () => void {
  const { ws, chat, banner, pauseBanner, scene, selfCharId, charNames, npcNames, onSelfHpChange, onCombatChange } = o;

  // Track every (type, handler) we register so the world-change teardown
  // can off() each one — the scene captured in these closures gets
  // destroyed across world transitions and would throw on the next event.
  const bindings: Array<[string, Handler]> = [];
  const bind = (type: string, h: Handler): void => {
    ws.on(type, h);
    bindings.push([type, h]);
  };

  bind('system.message', (msg: { text: string }) => chat.system(msg.text));
  bind('rule.established', (msg: { text: string; codex?: boolean }) => chat.system(
    msg.codex
      ? `📜 The GM inscribes a codex rule: ${msg.text}`
      : `📜 The GM establishes a house rule: ${msg.text}`,
  ));
  bind('rule.amended', (msg: { text: string }) => chat.system(
    `📜 The GM amends a codex rule: ${msg.text}`,
  ));
  bind('rules.paused', (msg: { topic: string }) => {
    pauseBanner.setPaused(msg.topic);
    chat.system(`⏸ The GM pauses to discuss: ${msg.topic}`);
  });
  bind('rules.resumed', (msg: { note: string | null }) => {
    pauseBanner.setPaused(null);
    chat.system(msg.note ? `▶ Play resumes — ${msg.note}` : '▶ Play resumes.');
  });
  bind('party.joined', (msg: { display_name: string }) => chat.system(`${msg.display_name} joined`));
  bind('presence.update', (msg: { users: PresenceUser[] }) => chat.setPresence(msg.users));
  bind('gm.narrate.start', () => chat.gmThinking());
  bind('gm.narrate.chunk', (msg: { text: string }) => chat.gmAppend(msg.text));
  bind('gm.narrate.end', () => chat.gmEnd());
  bind('character.moved', (msg: { character_id: number; q: number; r: number }) =>
    scene.moveCharacter(msg.character_id, msg.q, msg.r));
  bind('character.joined_party', (msg: { character_id: number; character_name: string; q: number; r: number }) => {
    scene.addCharacter({ id: msg.character_id, name: msg.character_name, q: msg.q, r: msg.r, isSelf: msg.character_id === selfCharId });
    charNames.set(msg.character_id, msg.character_name);
    chat.system(`${msg.character_name} joined the party`);
  });
  bind('character.left_party', (msg: { character_id: number }) => {
    scene.removeCharacter(msg.character_id);
    chat.system('a character left the party');
  });
  bind('hex.discovered', (msg: { q: number; r: number }) => scene.discover(msg.q, msg.r));
  bind('hex.condition.changed', (msg: { hex_id: number; q: number; r: number; place: string; condition: string }) => {
    const conditionText = getConditionNarrative(msg.condition, msg.place);
    if (conditionText) {
      chat.system(conditionText);
    }
  });
  bind('location.arrived', (msg: { character_id: number; place: string; first_visit: boolean; description: string | null }) => {
    const isSelf = msg.character_id === selfCharId;
    const who = isSelf ? 'You' : (charNames.get(msg.character_id) ?? 'Someone');
    if (msg.first_visit) {
      const verb = isSelf ? 'arrive' : 'arrives';
      chat.location(`${who} ${verb} at ${msg.place}.`);
      if (msg.description) chat.location(msg.description);
    } else {
      const verb = isSelf ? 'return' : 'returns';
      let text = `${who} ${verb} to ${msg.place}.`;
      if (msg.description) {
        text += `\n\n${msg.description}`;
      }
      chat.location(text);
    }
  });
  bind('npc.moved', (msg: { npc_id: number; q: number; r: number }) =>
    scene.moveNpc(msg.npc_id, msg.q, msg.r));

  bind('npc.state.changed', (msg: { npc_id: number; q: number; r: number; name: string; mood: string; rumor_count: number }) => {
    const moodText = getMoodDescription(msg.mood);
    chat.system(`${msg.name} seems ${moodText}. [${msg.rumor_count} rumors]`);
  });

  bind('combat.started', (msg: CombatSnapshot) => {
    for (const p of msg.participants) {
      if (p.kind === 'character' && p.character_id !== null) charNames.set(p.character_id, p.name);
      if (p.kind === 'npc' && p.npc_id !== null) npcNames.set(p.npc_id, p.name);
    }
    banner.setCombat(msg);
    chat.system(`combat begins — ${msg.participants.map((p) => p.name).join(', ')}`);
    onCombatChange();
  });
  bind('combat.ended', (msg: { reason: string }) => {
    banner.setCombat(null);
    chat.system(`combat ends — ${msg.reason}`);
    onCombatChange();
  });
  bind('combat.turn', (msg: { round_num: number; current_turn_idx: number; active: { name: string } }) => {
    banner.setTurn(msg.round_num, msg.current_turn_idx);
    chat.system(`round ${msg.round_num} — ${msg.active.name}'s turn`);
    onCombatChange();
  });
  bind('character.hp_changed', (msg: { character_id: number; hp: number; max_hp: number; delta: number; reason: string | null }) => {
    banner.updateCharacterHp(msg.character_id, msg.hp, msg.max_hp);
    if (msg.character_id === selfCharId) onSelfHpChange(msg.hp, msg.max_hp);
    const name = charNames.get(msg.character_id) ?? `character ${msg.character_id}`;
    const verb = msg.delta < 0 ? 'takes' : msg.delta > 0 ? 'heals' : 'is unchanged at';
    const amt = Math.abs(msg.delta);
    const reasonSuffix = msg.reason ? ` (${msg.reason})` : '';
    chat.hp(`${name} ${verb} ${amt} — ${msg.hp}/${msg.max_hp}${reasonSuffix}`, msg.hp === 0);
  });
  bind('npc.hp_changed', (msg: { npc_id: number; hp: number; max_hp: number; delta: number; reason: string | null; defeated: boolean }) => {
    banner.updateNpcHp(msg.npc_id, msg.hp, msg.max_hp, msg.defeated);
    const verb = msg.delta < 0 ? 'takes' : msg.delta > 0 ? 'heals' : 'is unchanged at';
    const amt = Math.abs(msg.delta);
    const reasonSuffix = msg.reason ? ` (${msg.reason})` : '';
    // Named, not numbered — "npc 91 takes 4" reads like a debugger, and this
    // line is most of what a player watches during a fight.
    const npcName = npcNames.get(msg.npc_id) ?? `npc ${msg.npc_id}`;
    chat.hp(`${npcName} ${verb} ${amt} — ${msg.hp}/${msg.max_hp}${reasonSuffix}`, msg.defeated);
  });
  bind('npc.died', (msg: { npc_id: number; q: number; r: number; name: string; world_id: number }) => {
    scene.removeNpc(msg.npc_id);
    chat.system(`${msg.name} falls in combat.`);
  });
  bind('character.leveled', (msg: { character_id: number; level: number; hp: number; max_hp: number }) => {
    // The system-message chat line is emitted server-side; here we just keep
    // the topbar HP (and combat banner, if mid-fight) in sync with the new max.
    if (msg.character_id === selfCharId) onSelfHpChange(msg.hp, msg.max_hp);
    banner.updateCharacterHp(msg.character_id, msg.hp, msg.max_hp);
  });
  // Server-resolved action: the prose lands as GM narration (same channel
  // arrival prose uses) with one compact maths line under it. No model call
  // produced either — the text came from a pre-written pool.
  bind('combat.action.resolved', (msg: {
    narration: string; outcome: string;
    attack_roll: number | null; attack_total: number | null; defence: number | null; damage: number | null;
  }) => {
    chat.location(msg.narration);
    if (msg.attack_roll !== null && msg.defence !== null) {
      const dmg = msg.damage !== null ? ` — ${msg.damage} damage` : '';
      chat.dice(`attack: d20 [${msg.attack_roll}] → ${msg.attack_total} vs ${msg.defence} — ${msg.outcome}${dmg}`);
    }
  });
  bind('combat.dice_rolled', (msg: { expression: string; breakdown: number[]; modifier: number; total: number; purpose: string | null }) => {
    const mod = msg.modifier === 0 ? '' : msg.modifier > 0 ? `+${msg.modifier}` : `${msg.modifier}`;
    const detail = `[${msg.breakdown.join(', ')}]${mod} = ${msg.total}`;
    chat.dice(`${msg.purpose ?? 'roll'}: ${msg.expression} → ${detail}`);
  });

  return () => {
    for (const [type, h] of bindings) ws.off(type, h);
  };
}

function getConditionNarrative(condition: string, place: string): string {
  switch (condition) {
    case 'flooded':
      return `Water suddenly flows across ${place}!`;
    case 'burned':
      return `${place} erupts in flames!`;
    case 'frozen':
      return `${place} freezes solid!`;
    case 'destroyed':
      return `${place} crumbles and collapses!`;
    default:
      return '';
  }
}

function getMoodDescription(mood: string): string {
  const moods: Record<string, string> = {
    'happy': 'quite happy',
    'angry': 'visibly angry',
    'sad': 'downcast',
    'neutral': 'neutral',
    'suspicious': 'suspicious',
  };
  return moods[mood] ?? mood;
}
